import React from 'react';
import { Card, CardContent, Typography, Box, Tooltip } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface StatCardProps {
    title: string;
    value: string | number;
    subtitle?: string;
    trend?: number;
    color?: 'primary' | 'success' | 'warning' | 'error' | 'info';
    icon?: React.ReactNode;
    info?: string;
}

export const StatCard: React.FC<StatCardProps> = ({
    title,
    value,
    subtitle,
    trend,
    color = 'primary',
    icon,
    info,
}) => {
    const colorMap = {
        primary: '#1976d2',
        success: '#4caf50',
        warning: '#ff9800',
        error: '#f44336',
        info: '#2196f3',
    };

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                            <Typography color="textSecondary" variant="body2">
                                {title}
                            </Typography>
                            {info && (
                                <Tooltip title={info} arrow placement="top">
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                                </Tooltip>
                            )}
                        </Box>
                        <Typography variant="h4" component="div" sx={{ color: colorMap[color], fontWeight: 'bold' }}>
                            {value}
                        </Typography>
                        {subtitle && (
                            <Typography variant="body2" color="textSecondary">
                                {subtitle}
                            </Typography>
                        )}
                        {trend !== undefined && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                {trend >= 0 ? (
                                    <TrendingUpIcon sx={{ color: '#4caf50', fontSize: 16, mr: 0.5 }} />
                                ) : (
                                    <TrendingDownIcon sx={{ color: '#f44336', fontSize: 16, mr: 0.5 }} />
                                )}
                                <Typography
                                    variant="body2"
                                    sx={{ color: trend >= 0 ? '#4caf50' : '#f44336' }}
                                >
                                    {Math.abs(Number(trend) || 0).toFixed(1)}%
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    {icon && (
                        <Box sx={{ color: colorMap[color], opacity: 0.3, fontSize: 48 }}>
                            {icon}
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default StatCard;
